import { BreakpointObserver } from '@angular/cdk/layout';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { map, tap } from 'rxjs';
import { GoogleAd } from 'src/app/core/enums';
import { PlaceAdService } from 'src/app/core/services/place-ad.service';
import { ApiService } from '@shared/services/api.service';
import { AuthService } from '@shared/services/auth.service';
import { LocaleService } from '@shared/services/locale.service';
import { ScrollService } from '@shared/services/scroll.service';
import { ViewportService } from '@shared/services/viewport.service';
import { User } from '@core/models';

@Component({
	selector: 'app-ads',
	templateUrl: './ads.component.html',
	styleUrls: ['./ads.component.scss'],
})
export class AdsComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('carousel') carousel: any;
	@ViewChild('scrollTrigger', { read: ElementRef }) scrollTrigger: ElementRef;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private title: Title,
		private auth: AuthService,
		private api: ApiService,
		private scroll: ScrollService,
		public locale: LocaleService,
		public viewport: ViewportService,
		private breakpointObserver: BreakpointObserver,
		private placeAd: PlaceAdService
	) {
		// force route reload whenever params/queries change
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
	}

	loading: boolean = true;
	page: number = 1;
	count: number = 0;
	user: User = this.auth.user();
	ads: any;
	params: Params;

	readonly googleAd = GoogleAd;
	readonly googleAdEnv = environment.GOOGLE_ADSENSE;
	isMoreAdsLoading: boolean = false;
	private intersectionObserver: IntersectionObserver | undefined;
	private MAX_RETRIES = 10; // Set your maximum retry attempts
	private retryCount = 0;
	isFilterReset: boolean = false;
	sortBy: any = { sortValue: this.locale.translations['ads']['filter']['n32']['n32-2'], clicked: false };
	selectedSortOption: number;

	prevent(event: any) {
		//    console.log(event);
		event.stopPropagation();
		event.preventDefault();
	}

	async fetchAds(startPage: number, endPage: number, paramsData: any) {
		for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
			await this.fetchData(pageNumber, paramsData);
		}
	}

	async fetchData(pageNumber: number, paramsData) {
		return new Promise<void>((resolve, reject) => {
			const formData: FormData = new FormData();

			if (this.user) formData.append('userID', paramsData['userID']);

			Object.keys(paramsData).forEach((query) => {
				query === 'category' ? formData.append('catID', paramsData[query]) : formData.append(query, paramsData[query]);
			});

			//get ads
			this.api
				.ads(formData, this.router.url, pageNumber ? pageNumber : 1)
				.pipe(
					map((response: any) => {
						const newResponse = { ...response };
						let newAdList = [...response['response']['data']];

						newAdList = newAdList.filter((adList: any) => !adList?.isGoogleAd);

						// Add Google Ad
						newAdList = this.addGoogleAd(newAdList);
						newResponse['response']['data'] = [...newAdList];
						return {
							...newResponse,
						};
					}),
					tap((response: any) => {
						// Show the place ad fixed bottom button if there's no ad list
						this.placeAd.setTogglePlaceAdMobile(!response['response']['data']?.length);
					})
				)
				.subscribe(
					(res) => {
						const newData = res['response']['data'];
						if (this.ads) {
							this.ads.data = this.ads.data.concat(newData);
							this.ads.pages = res['response']['pages'];
							this.ads.totalRows = res['response']['totalRows'];
							this.page = this.page + 1;
						} else {
							// If ads is empty or doesn't exist, set it to the new data
							this.ads = {
								data: newData,
								pages: res['response']['pages'],
								totalRows: res['response']['totalRows'],
							};
						}
						resolve(); // Resolve the Promise once API call is completed

						this.loading = false;
						this.isMoreAdsLoading = false;

						// scroll to stored position
						const _scroll = this.scroll.fetch();
						if (_scroll !== undefined && _scroll?.['trigger'] === true) {
							setTimeout(() => {
								window.scrollTo({ left: _scroll['xpos'], top: _scroll['ypos'] });
							}, 0);
						}

						// clear scrol
						this.scroll.save(undefined, undefined, undefined, undefined);
					},
					(error) => {
						reject(error); // Reject the Promise with the error
					}
				);
		});
	}

	fetchAdsOnScroll() {
		if (this.ads?.['pages'] > this.page) {
			this.isMoreAdsLoading = true;
			this.params = {
				...this.params,
				page: this.page + 1,
			};

			this.scroll.saveAdListPageNo(this.params?.page);

			this.fetchAds(this.params?.page, this.params?.page, this.params);
		}
	}

	private initIntersectionObserver(): void {
		this.intersectionObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry: IntersectionObserverEntry) => {
					if (entry.isIntersecting) {
						this.fetchAdsOnScroll();
					}
				});
			},
			{ rootMargin: '48px', threshold: 0.1 } // Adjust threshold as needed
		);

		this.intersectionObserver.observe(this?.scrollTrigger?.nativeElement);
	}

	private retryInitIntersectionObserver() {
		if (this.scrollTrigger && this.scrollTrigger.nativeElement instanceof Element) {
			// Trigger intersection observer setup
			this.initIntersectionObserver();
		} else {
			// Retry logic
			if (this.retryCount < this.MAX_RETRIES) {
				this.retryCount++;
				setTimeout(() => this.retryInitIntersectionObserver(), 1000); // Retry after 1 second
			} else {
				console.error(' scrollTrigger is not available.');
			}
		}
	}

	/**
	 * Check if the ancestor element is intersecting to each other
	 *
	 * @param isIntersecting Boolean if two elements are intersecting
	 */
	onAdsPaginationIntersect(isIntersecting: boolean, isAdListIntersecting: boolean) {
		this.placeAd.setTogglePlaceAdMobile(!isIntersecting && isAdListIntersecting);
	}

	onGoogleAdBannerIntersect(isIntersecting: boolean, isAdListIntersecting: boolean) {
		if (this.ads['pages'] === 1) {
			this.placeAd.setTogglePlaceAdMobile(!isIntersecting && isAdListIntersecting);
		}
	}

	/**
	 * Add Google Ad in Ad list
	 *
	 * @param adList List of ads
	 * @returns List of ads with Google Ad
	 */
	private addGoogleAd(adList: any): any[] {
		let adListWithGoogleAds = [];

		if (!adList?.length) {
			return adListWithGoogleAds;
		}

		const googleAdPosition = 8;
		const googleAd = { isGoogleAd: true };

		// Add the initial google ad in 7th position
		if (adList.length >= 7) {
			// Add the initial Google ad after the 7th ad
			adListWithGoogleAds.push(...adList.slice(0, 6), { ...googleAd });
			adList = adList.slice(6); // Exclude the first 7 elements

			// Add Google ad after every 8th position
			for (let i = 0; i < adList.length; i++) {
				adListWithGoogleAds.push(adList[i]);
				if ((i + 1) % googleAdPosition === 0 && i !== adList.length - 1) {
					adListWithGoogleAds.push({ ...googleAd });
				}
			}
		} else {
			// If there are less than 7 ads, just add all ads in adListWithGoogleAds
			adListWithGoogleAds.push(...adList);
		}
		return adListWithGoogleAds;
	}

	onReset() {
		this.isFilterReset = true;
	}

	onOpenSort(sortValue: number, clicked: boolean) {
		let sortString;

		this.selectedSortOption = sortValue; // Update the selected sort option

		switch (sortValue) {
			case 1:
				sortString = this.locale.translations['ads']['filter']['n32']['n32-2'];
				break;
			case 0:
				sortString = this.locale.translations['ads']['filter']['n32']['n32-3'];
				break;
			case 2:
				sortString = this.locale.translations['ads']['filter']['n32']['n427'];
				break;
			case 3:
				sortString = this.locale.translations['ads']['filter']['n32']['n426'];
				break;

			default:
				sortString = this.locale.translations['ads']['filter']['n32']['n32-2'];
				break;
		}
		this.sortBy = { sortValue: sortString, clicked: clicked };
	}

	ngOnInit(): void {
		this.title.setTitle(this.locale.translations['tabbar']['n155'] + ' | ' + this.locale.translations['header']['n0']);
		this.auth.bootIntercom(true);
		const PAGE_NO = this.scroll.getAdListPageNo();

		this.route.params.subscribe((params: Params) => {
			if (params['category']) {
				this.route.queryParams.subscribe((queries: Params) => {
					const updatedQueries = { ...queries }; // Create a copy of queries

					if (queries['order']) {
						this.onOpenSort(+queries['order'], false); //making false here so that onSubmit() function in filter does not get called again
					} else {
						this.selectedSortOption = 1; // Update the selected sort option
					}

					if (queries['catID']) {
						params = { ...params, category: queries['catID'] };
						delete updatedQueries['catID']; // Remove catID from the updatedQueries object
					}

					this.params = {
						...params,
						...updatedQueries,
						page: this.page,
						...(this.user ? { userID: this.user['userID'] } : {}),
					};
				});
			} else {
				// 404 page not found
				this.router.navigate(['/page/404']);
			}
		});

		if (PAGE_NO) {
			setTimeout(() => {
				this.fetchAds(1, PAGE_NO, this.params);
			}, 300);
		} else {
			setTimeout(() => {
				this.fetchAds(1, 1, this.params);
			}, 300);
		}

		//hide chat intercom for mobile view
		this.breakpointObserver.observe(['(max-width: 480px)', '(max-width: 840px)']).subscribe((res) => {
			if (res['breakpoints']['(max-width: 480px)'] === true) {
				this.auth.bootIntercom(true);
			} else {
				this.auth.bootIntercom(false);
			}
		});
	}

	ngAfterViewInit() {
		//somehow scrollTrigger Element is not getting renedered intially in ngAfterViewInit so used this logic of retry
		this.retryInitIntersectionObserver();
	}

	//On destroy
	ngOnDestroy(): void {
		this.auth.bootIntercom(false);
		this.placeAd.setTogglePlaceAdMobile(false);
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect();
		}
	}
}